import type { ElementRef, QueryList } from '@angular/core';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    type OnChanges,
    type OnDestroy,
    type OnInit,
    type SimpleChanges,
    ViewChild,
    ViewChildren,
    inject,
} from '@angular/core';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { filter, Subject, takeUntil } from 'rxjs';
import type { ProductCardWidgetData } from '@web/widgets/product-card/type';
import { CardKindViewType, type GallerySettings } from '@web/widgets/product-card/type';
import type Swiper from 'swiper';
import { type SwiperOptions } from 'swiper';
import { ResizableModule, type ResizeEvent } from 'angular-resizable-element';
import { type SelectedFileExtended } from '@common/types/file.type';
import { type WebWidgetData } from '@web/types';
import { themeColorCss } from '@sites/site-theme/src/utils/variables';
import { AspectRatio, Attachment } from '@web/widgets/product-card/type/product-card-widget.type';
import { type ProductCard } from '@common/types/product-card.type';
import { SwiperModule } from 'swiper/angular';
import { CommonModule } from '@angular/common';
import { SppFallbackImgDirective } from '@common/directives/fallback-img.directive';
import { environment } from '@web-env/environment';
import { formatFileUrl, formatFileUrlWithResize, resolveCdnBaseUrl } from '@components/sp-file-manager/src/helpers';

@Component({
    selector: 'web-gallery-views',
    templateUrl: './gallery-views.component.html',
    styleUrls: ['./gallery-views.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, SwiperModule, ResizableModule, SppFallbackImgDirective],
})
export class GalleryViewsComponent implements OnInit, OnChanges, OnDestroy {
    protected readonly widgetsFacade = inject(WidgetsFacade);
    protected readonly changeDetectorRef = inject(ChangeDetectorRef);

    @Input() widgetId: string;
    @Input() data: ProductCardWidgetData;
    @Input() isDeviceMobile: boolean;

    @ViewChildren('resizeBox') resizeBox: QueryList<ElementRef<HTMLElement>>;
    @ViewChild('dragHandleBottom') dragHandleBottom: ElementRef;
    @ViewChild('imagesContainer') imagesContainer: ElementRef;

    protected unsubscribe$ = new Subject<void>();

    public attachment = Attachment;
    public aspectRatio = AspectRatio;

    mainSwiper: Swiper;
    thumbsSwiper: Swiper;
    swiperConfig: SwiperOptions = {};
    thumbsParentConfig: SwiperOptions = {};

    imagePosition = { x: 50, y: 50 };
    cursorPosBefore = { x: 0, y: 0 };
    imagePosBefore = null;
    imagePosAfter = null;
    isMouseDown = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isDeviceMobile'] && !changes['isDeviceMobile'].firstChange) {
            this.thumbsSwiper = null;
            this.thumbsParentConfig = {};
            this.mainSwiper = null;
            this.swiperConfig = {};
        }
    }

    ngOnInit() {
        this.widgetsFacade
            .widgetById$(this.widgetId)
            .pipe(takeUntil(this.unsubscribe$), filter(Boolean))
            .subscribe((widget) => {
                const widgetData = widget.data as ProductCardWidgetData;

                const { changeSlide } = widgetData.cardSettings.gallery;

                if (!this.isShowThumbs && this.thumbsSwiper) {
                    this.thumbsSwiper.destroy();
                    this.thumbsSwiper = null;
                }

                if (changeSlide && this.mainSwiper) {
                    this.swiperConfig.autoplay = this.thumbsParentConfig.autoplay = {
                        delay: 2000,
                        pauseOnMouseEnter: true,
                        disableOnInteraction: false,
                    };

                    this.mainSwiper?.autoplay?.start();
                } else {
                    this.swiperConfig.autoplay = this.thumbsParentConfig.autoplay = false;
                    this.mainSwiper?.autoplay?.stop();
                }

                this.changeDetectorRef.detectChanges();
            });
    }

    public setSwiper(swiper: Swiper) {
        this.mainSwiper = swiper;

        if (this.gallerySettings.changeSlide) {
            this.swiperConfig = {
                autoplay: {
                    delay: 2000,
                    pauseOnMouseEnter: true,
                    disableOnInteraction: false,
                },
            };
            this.mainSwiper.autoplay?.start();
        }

        this.changeDetectorRef.detectChanges();
    }

    public setThumbs(swiper: Swiper) {
        this.thumbsSwiper = swiper;
        this.thumbsParentConfig = {
            thumbs: {
                swiper: swiper,
            },
        };

        this.changeDetectorRef.detectChanges();
    }

    public resizeOnload(any: HTMLImageElement, target: HTMLElement, index: number) {
        if (this.cardImages[index].resizedHeight) return;

        let newPadding = (any.naturalHeight / any.naturalWidth) * 100;

        if (this.isGalleryViewTypeFirst) {
            newPadding = this.getImagePaddingTop(this.cardImages[index]);
        }

        target.style.paddingTop = newPadding + '%';

        if (this.isCollageView) {
            if (this.isMasonry) {
                this.syncWidgetData({
                    element: {
                        ...this.element,
                        images: this.cardImages?.map((image, i) => {
                            if (i === index) {
                                return {
                                    ...image,
                                    resizedHeight: any.naturalHeight,
                                    resizedWidth: any.naturalWidth,
                                };
                            }
                            return image;
                        }),
                    },
                });
                return;
            }
        }

        this.syncWidgetData({
            element: {
                ...this.element,
                images: this.cardImages?.map((image) => {
                    return {
                        ...image,
                        resizedHeight: any.naturalHeight,
                        resizedWidth: any.naturalWidth,
                    };
                    return image;
                }),
            },
        });
    }

    public onResizeStart(event: ResizeEvent, target: HTMLElement): void {}
    public onResizing(event: ResizeEvent, target: HTMLElement): void {
        if (this.isCollageView) {
            if (this.isMasonry) {
                if (typeof event.edges.bottom !== 'number') return;
                if (event.rectangle.height <= 50) return;
                const newPadding = (event.rectangle.height / event.rectangle.width) * 100;
                target.style.paddingTop = newPadding + '%';
                return;
            }
        }

        this.resizeBox.forEach(({ nativeElement }) => {
            if (typeof event.edges.bottom !== 'number') return;
            if (event.rectangle.height <= 50) return;
            const newPadding = (event.rectangle.height / event.rectangle.width) * 100;
            nativeElement.style.paddingTop = newPadding + '%';
        });
    }
    public onResizeEnd(event: ResizeEvent, target: HTMLElement, index: number): void {
        if (this.isCollageView) {
            if (this.isMasonry) {
                this.updateWidgetData({
                    element: {
                        ...this.element,
                        images: this.cardImages?.map((image, i) => {
                            if (i === index) {
                                return {
                                    ...image,
                                    resizedHeight: event.rectangle.height,
                                    resizedWidth: event.rectangle.width,
                                };
                            }
                            return image;
                        }),
                    },
                });
                return;
            }
        }

        this.updateWidgetData({
            element: {
                ...this.element,
                images: this.cardImages?.map((image) => {
                    return {
                        ...image,
                        resizedHeight: event.rectangle.height,
                        resizedWidth: event.rectangle.width,
                    };
                    return image;
                }),
            },
        });
    }

    public mouseUp(index: number): void {
        if (!this.isMouseDown) return;
        this.isMouseDown = false;
        this.updateWidgetData({
            element: {
                ...this.element,
                images: this.cardImages?.map((image, i) => {
                    if (i === index) {
                        return {
                            ...image,
                            backgroundPositionX: this.imagePosAfter.x,
                            backgroundPositionY: this.imagePosAfter.y,
                        };
                    }
                    return image;
                }),
            },
        });
    }

    public mouseDown(event: MouseEvent, image: SelectedFileExtended): void {
        event.preventDefault();
        this.imagePosition = {
            x: image.backgroundPositionX || 50,
            y: image.backgroundPositionY || 50,
        };
        this.isMouseDown = true;
        this.imagePosAfter = this.imagePosition;
        this.imagePosBefore = this.imagePosAfter;
        this.cursorPosBefore = { x: event.pageX, y: event.pageY };
    }

    public move(event: MouseEvent, imageRef: HTMLImageElement): void {
        event.preventDefault();
        if (event.buttons !== 1 || !this.imagePosBefore) return;

        const newXPos = this.imagePosBefore.x + ((this.cursorPosBefore.x - event.clientX) / imageRef.width) * 100;
        const newYPos = this.imagePosBefore.y + ((this.cursorPosBefore.y - event.clientY) / imageRef.height) * 100;

        if (newXPos < 0 || newXPos > 100 || newYPos < 0 || newYPos > 100) {
            return;
        }
        this.imagePosAfter = { x: newXPos, y: newYPos };

        (event.target as HTMLImageElement).style.objectPosition = `${newXPos}% ${newYPos}%`;
    }

    public getImagePaddingTop(image: SelectedFileExtended): number {
        const galleryImageRatio: AspectRatio = this.gallerySettings.aspectRatio;

        switch (galleryImageRatio) {
            case AspectRatio.default:
                return 100;
            case AspectRatio.manual:
                if (!image.resizedHeight || !image.resizedWidth) {
                    return 100;
                }
                return (image.resizedHeight / image.resizedWidth) * 100;
            case AspectRatio.onoToOne:
                return 100;
            case AspectRatio.fourToThree:
                return 75;
            case AspectRatio.threeToFour:
                return 133;
            case AspectRatio.threeToTwo:
                return 66;
            case AspectRatio.twoToThree:
                return 150;
            case AspectRatio.verticalWide:
                return 177;
            case AspectRatio.wide:
                return 56;
        }
    }

    public formatImageURL(image: SelectedFileExtended): string {
        return formatFileUrlWithResize(image, this.getImageBaseUrl(image), 720);
    }

    public formatImageFallbackURL(image: SelectedFileExtended): string {
        return formatFileUrl(image, this.getImageBaseUrl(image));
    }

    private getImageBaseUrl(image: SelectedFileExtended): string {
        return resolveCdnBaseUrl(image?.defaultCdnUrl, environment.fileManagerCdnUrl);
    }

    private updateWidgetData(data: Partial<WebWidgetData>): void {
        this.widgetsFacade.updateWidgetData(this.widgetId, data);
    }

    private syncWidgetData(data: Partial<WebWidgetData>): void {
        this.widgetsFacade.syncLegacyWidgetProperties(this.widgetId, { data: data as WebWidgetData });
    }

    public get isHasBottomSpace(): boolean {
        return this.cardImages.length > 1;
    }

    public get loopedSlides(): number {
        return this.cardImages.length === 3 ? this.cardImages.length : this.cardImages.length - 1;
    }

    public get isShowThumbs(): boolean {
        return [CardKindViewType.second, CardKindViewType.fourth].includes(this.data.cardKindViewType);
    }

    public get isCollageView(): boolean {
        return [CardKindViewType.sixth, CardKindViewType.seventh].includes(this.data.cardKindViewType);
    }

    public get masonryArrays(): SelectedFileExtended[][] {
        const columns = 2;
        const arr = [...Array(columns)].map(() => []);

        for (let i = 0; i < this.cardImages.length; i++) {
            arr[i % columns].push(this.cardImages[i]);
        }

        return arr;
    }

    public get isMasonry(): boolean {
        return this.data.cardKindViewType === CardKindViewType.sixth;
    }

    public get isSixth(): boolean {
        return this.data.cardKindViewType === CardKindViewType.sixth;
    }

    public get isVerticalSwiper(): boolean {
        return [CardKindViewType.third, CardKindViewType.fourth].includes(this.data.cardKindViewType);
    }

    public get element(): ProductCard {
        return this.data.element;
    }

    public get cardImages() {
        return this.element.images;
    }

    public get gallerySettings(): GallerySettings {
        return this.data.cardSettings.gallery as GallerySettings;
    }

    instanceId = (index: number): number => index;

    public get isGalleryViewTypeFirst(): boolean {
        return this.data.cardKindViewType === CardKindViewType.first;
    }

    public get isGalleryViewTypeSecond(): boolean {
        return this.data.cardKindViewType === CardKindViewType.second;
    }

    public get isGalleryViewTypeFifth(): boolean {
        return this.data.cardKindViewType === CardKindViewType.fifth;
    }

    public get isGalleryViewTypeSixth(): boolean {
        return this.data.cardKindViewType === CardKindViewType.sixth;
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    protected readonly themeColorCss = themeColorCss;
    protected readonly CardKindViewType = CardKindViewType;
}
